/**
 * Module: `queries/conversation-feedbacks.ts` — per-conversation feedback hydration.
 */

import { queryOptions } from "@tanstack/react-query";

import { SESSION_SCOPED_USER_QUERY_KEY } from "@/constants";
import { apiClient } from "@/lib/api";
import {
  isEphemeralChatConversationId,
  isMockDustConversationId,
} from "@/lib/queries/conversation";
import { minutesTOms } from "@/lib/utils";
import { queryClient } from "@/tanstack-db/query-client";

export type StoredMessageFeedback = {
  thumbDirection: "up" | "down";
  feedbackContent: string | null;
  motifs: Array<{ code: string; label: string }>;
};

export type MessageFeedbacksByMessageId = Record<string, StoredMessageFeedback>;

export function conversationFeedbacksQueryKey(conversationId: string) {
  return [
    "conversation-feedbacks",
    SESSION_SCOPED_USER_QUERY_KEY,
    conversationId,
  ] as const;
}

export function conversationFeedbacksQueryOptions(conversationId: string) {
  return queryOptions({
    queryKey: conversationFeedbacksQueryKey(conversationId),
    queryFn: () => apiClient.getConversationFeedbacks(conversationId),
    staleTime: minutesTOms(2),
    gcTime: minutesTOms(30),
  });
}

export function messageFeedbacksFromResponse(response: {
  feedbacks: {
    dustMessageId: string;
    thumbDirection: "up" | "down";
    feedbackContent: string | null;
    motifs?: Array<{ code: string; label: string }> | null;
  }[];
}): MessageFeedbacksByMessageId {
  const next: MessageFeedbacksByMessageId = {};
  for (const feedback of response.feedbacks) {
    next[feedback.dustMessageId] = {
      thumbDirection: feedback.thumbDirection,
      feedbackContent: feedback.feedbackContent,
      motifs: feedback.motifs ?? [],
    };
  }
  return next;
}

export async function prefetchConversationFeedbacks(
  conversationId: string
): Promise<void> {
  if (
    !conversationId ||
    isEphemeralChatConversationId(conversationId) ||
    isMockDustConversationId(conversationId)
  ) {
    return;
  }

  await queryClient.fetchQuery(
    conversationFeedbacksQueryOptions(conversationId)
  );
}
